import { useState, useRef, useCallback } from 'react';
import { cn } from '@lib/utils';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps {
  content: string;
  position?: TooltipPosition;
  children: React.ReactNode;
  className?: string;
}

interface Coords {
  top: number;
  left: number;
}

const OFFSET = 8;
const TOOLTIP_WIDTH = 224; // w-56 = 14rem = 224px

function calcCoords(rect: DOMRect, position: TooltipPosition): Coords {
  switch (position) {
    case 'top':
      return {
        top: rect.top - OFFSET,
        left: rect.left + rect.width / 2,
      };
    case 'bottom':
      return {
        top: rect.bottom + OFFSET,
        left: rect.left + rect.width / 2,
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + OFFSET,
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - OFFSET,
      };
  }
}

const transformMap: Record<TooltipPosition, string> = {
  top: 'translate(-50%, -100%)',
  bottom: 'translate(-50%, 0)',
  right: 'translate(0, -50%)',
  left: 'translate(-100%, -50%)',
};

const arrowClasses: Record<TooltipPosition, string> = {
  top: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-t-gray-900 border-x-transparent border-b-transparent border-4',
  bottom: 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-b-gray-900 border-x-transparent border-t-transparent border-4',
  right: 'left-0 top-1/2 -translate-y-1/2 -translate-x-full border-r-gray-900 border-y-transparent border-l-transparent border-4',
  left: 'right-0 top-1/2 -translate-y-1/2 translate-x-full border-l-gray-900 border-y-transparent border-r-transparent border-4',
};

export function Tooltip({ content, position = 'top', children, className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    // Clamp right-side tooltips so they don't overflow the viewport
    let resolved = position;
    if (position === 'right' && rect.right + OFFSET + TOOLTIP_WIDTH > window.innerWidth) {
      resolved = 'left';
    }

    setCoords(calcCoords(rect, resolved));
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={cn('inline-flex items-center', className)}
      >
        {children}
      </span>

      {visible && (
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: transformMap[position],
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          className="w-56 rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-xl"
        >
          <span className={cn('absolute border-4', arrowClasses[position])} />
          {content}
        </div>
      )}
    </>
  );
}
